import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import SearchComponent from '../../components/ui/SearchComponent';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { eventService } from '../../utils/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const EventsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    featured: false
  });
  const [error, setError] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/city-official-dashboard' },
    { label: 'Events' }
  ];

  const eventTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'conference', label: 'Conference' },
    { value: 'webinar', label: 'Webinar' },
    { value: 'hackathon', label: 'Hackathon' },
    { value: 'networking', label: 'Networking' },
    { value: 'training', label: 'Training' },
    { value: 'showcase', label: 'Showcase' },
    { value: 'meeting', label: 'Meeting' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Events' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Past' },
    { value: 'today', label: 'Today' }
  ];

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [events, searchQuery, filters]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: eventsError } = await eventService?.getAllEvents();
      
      if (eventsError) {
        setError('Failed to load events');
        return;
      }
      
      setEvents(data || []);
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...events];

    // Apply search
    if (searchQuery) {
      filtered = filtered?.filter(event =>
        event?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        event?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        event?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply type filter
    if (filters?.type !== 'all') {
      filtered = filtered?.filter(event => event?.event_type === filters?.type);
    }

    // Apply status filter
    if (filters?.status !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

      filtered = filtered?.filter(event => {
        const eventDate = new Date(event?.start_date);
        const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        
        switch (filters?.status) {
          case 'upcoming':
            return eventDate > now;
          case 'past':
            return eventDate < now;
          case 'today':
            return eventDay?.getTime() === today?.getTime();
          default:
            return true;
        }
      });
    }

    // Apply featured filter
    if (filters?.featured) {
      filtered = filtered?.filter(event => event?.is_featured);
    }

    // Sort by start date
    filtered?.sort((a, b) => new Date(a?.start_date) - new Date(b?.start_date));

    setFilteredEvents(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      type: 'all',
      status: 'all',
      featured: false
    });
    setSearchQuery('');
  };

  const handleEventClick = (eventId) => {
    navigate(`/event-details/${eventId}`);
  };

  const getEventStatusBadge = (event) => {
    const now = new Date();
    const startDate = new Date(event?.start_date);
    const endDate = event?.end_date ? new Date(event?.end_date) : null;

    if (endDate && now > endDate) {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">Past</span>;
    } else if (now < startDate) {
      return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Upcoming</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">Happening Now</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 lg:px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading events...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 lg:px-6">
        <div className="mb-6">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Smart Cities Events</h1>
              <p className="text-muted-foreground mt-1">
                Discover workshops, conferences, and networking opportunities
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SearchComponent
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search events..."
              className="w-full"
            />
            
            <Select
              options={eventTypeOptions}
              value={filters?.type}
              onChange={(value) => handleFilterChange('type', value)}
              placeholder="Event Type"
            />
            
            <Select
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
              placeholder="Status"
            />
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters?.featured}
                  onChange={(e) => handleFilterChange('featured', e?.target?.checked)}
                  className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm text-foreground">Featured only</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {filteredEvents?.length} of {events?.length} events
            </p>
            
            {(searchQuery || Object.values(filters)?.some(v => v !== 'all' && v !== false)) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
              >
                <Icon name="X" size={14} className="mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Events Grid */}
        {filteredEvents?.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredEvents?.map((event) => (
              <div
                key={event?.id}
                onClick={() => handleEventClick(event?.id)}
                className="bg-card border border-border rounded-lg p-6 cursor-pointer transition-all hover:shadow-md hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getEventStatusBadge(event)}
                      {event?.is_featured && (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          Featured
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
                      {event?.title}
                    </h3>
                  </div>
                  <Icon 
                    name={event?.is_virtual ? 'Monitor' : 'MapPin'} 
                    size={20} 
                    className="text-muted-foreground flex-shrink-0 ml-2" 
                  />
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {event?.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Icon name="Calendar" size={14} className="mr-2 flex-shrink-0" />
                    <span className="truncate">
                      {format(new Date(event?.start_date), 'MMM dd, yyyy')} at{' '}
                      {format(new Date(event?.start_date), 'h:mm a')}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Icon name={event?.is_virtual ? 'Monitor' : 'MapPin'} size={14} className="mr-2 flex-shrink-0" />
                    <span className="truncate">
                      {event?.is_virtual ? 'Virtual Event' : event?.location}
                    </span>
                  </div>

                  {event?.max_participants && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Icon name="Users" size={14} className="mr-2 flex-shrink-0" />
                      <span>
                        {event?.current_participants || 0} / {event?.max_participants} registered
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 text-xs font-medium bg-muted text-muted-foreground rounded capitalize">
                      {event?.event_type?.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <Button variant="ghost" size="sm">
                    View Details
                    <Icon name="ChevronRight" size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Calendar" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No events found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || Object.values(filters)?.some(v => v !== 'all' && v !== false)
                ? "Try adjusting your search or filters to find more events." :"There are no events available at the moment."
              }
            </p>
            {(searchQuery || Object.values(filters)?.some(v => v !== 'all' && v !== false)) && (
              <Button variant="outline" onClick={clearFilters}>
                <Icon name="RefreshCw" size={14} className="mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default EventsPage;